'use strict';

const boardingOptionsCollection = require('../../modules/collections/boarding_options/boarding_options.collection').getInstance();
const BOARDING_TYPES = new Odigeo.Enum(['BO_SB', 'BO_PB', 'BO_HB']);

module.exports = {

    loadModel(data) {
        this.set(data);
        this.setPriorityOptions();
        this.setHighlightedType();
        this.processStoredSelection();
        this.widget.view.loadView();
    },

    getPrimeInfo() {
        return boardingOptionsCollection.getPrimeInfo();
    },

    extendOption(option) {
        const code = option.type;
        const icons = this.getIcons();

        return Odigeo._.extend(option, {
            type: code,
            name: this.getContentKey('_title', code),
            size: this.getContentKey('_size', code),
            services: this.getServices()[code],
            selectText: this.getContentKey('_select', code),
            selectedText: this.getContentKey('_selected', code),
            price: option.totalPrice,
            primePrice: option.totalPrimePrice,
            icon: Odigeo._.get(icons[code], 'standard'),
            confirmationIcon: Odigeo._.get(icons[code], 'confirmation'),
        });
    },

    getCarrierSettings(attribute) {
        return Odigeo._.get(boardingOptionsCollection.getCarrierSettings(), attribute, {});
    },

    getIcons() {
        return this.getCarrierSettings('icons');
    },

    getCarrierHighlightedType() {
        return this.getCarrierSettings('highlightedType');
    },

    getSortingConditions() {
        return this.getCarrierSettings('sortingConditions');
    },

    processStoredSelection() {
        const storedSelection = this.getStoredSelection();

        this.selectType(storedSelection || this.getDefaultType());

        if (!Odigeo._.isEmpty(storedSelection)) {
            boardingOptionsCollection.setSelectedByUser();
            this.triggerUserInteraction();
        }
    },

    getStoredSelection() {
        const sessionSelection = this.getSessionSelection();

        return !Odigeo._.isEmpty(sessionSelection)
            ? sessionSelection
            : this.getSelectionInCart();
    },

    getSelectionInCart() {
        return Odigeo._.get(this.get('selection') || [], '[0].type');
    },

    getSessionSelection() {
        return Odigeo._.get(Odigeo.Utils.parseJson(Odigeo.Session.get('boardingOptionSelection')), this.getBookingId());
    },

    getBookingId() {
        return Odigeo._.get(this.widget, 'options.bookingId');
    },

    setPriorityOptions() {
        const options = Odigeo._.mapValues(this.get('collection'), function(option) {
            return this.extendOptionInfo(option);
        }, this);

        this.set('options', options);
    },

    getHighlightedText(code, isHighlighted) {
        return isHighlighted && code === BOARDING_TYPES.BO_PB
            ? this.getContentKey('most_popular')
            : this.getContentKey('recommended');
    },

    getPriorityOptions() {
        const highlightedType = this.getHighlightedType();

        return Odigeo._.mapValues(this.getValidOptions(), function(option) {
            const boardingType = option.type;
            const isHighlighted = highlightedType === boardingType;

            return Odigeo._.extend(this.extendOptionInfo(option), {
                isSelected: this.isSelected(boardingType),
                isHighlighted: isHighlighted,
                highlightedText: this.getHighlightedText(boardingType, isHighlighted),
            });
        }, this);
    },

    transformArrayToObject(options) {
        return Odigeo._.transform(options, function(result, option) {
            result[option.type] = option;
        });
    },

    getPriorityOptionsObject() {
        return this.transformArrayToObject(this.getPriorityOptions());
    },

    getValidOptions() {
        return Odigeo._.union(this.getFilteredOptions(), this.getAllMissingOptions());
    },

    getFilteredOptions() {
        return Odigeo._.filter(this.get('options'), function(option) {
            return !Odigeo._.isEmpty(BOARDING_TYPES[option.type]);
        }, this);
    },

    getAllMissingOptions() {
        const options = this.transformArrayToObject(this.get('options'));

        return Odigeo._.map(Odigeo._.filter(BOARDING_TYPES, function(type) {
            return !Odigeo._.has(options, type);
        }), function(type) {
            return {
                type: type,
                available: false,
            };
        });
    },

    getAvailableOptions() {
        return Odigeo._.sortBy(this.getPriorityOptions(), function(option) {
            return Odigeo._.findKey(this.getSortingConditions(), function(condition) {
                return Odigeo._.every(condition, function(value, key) {
                    return option[key] === value;
                });
            });
        }, this);
    },

    isSelected(type) {
        return this.getSelectedType() === type;
    },

    setHighlightedType() {
        const priorityOptions = this.getPriorityOptionsObject();
        const highlightedType = Odigeo._.findKey(this.getCarrierHighlightedType(), function(conditions) {
            return Odigeo._.every(conditions, function(condition) {
                return Odigeo._.get(priorityOptions, [condition.type, 'available']) === condition.available;
            });
        });

        this.set('highlightedType', highlightedType);
    },

    getHighlightedType() {
        return this.get('highlightedType');
    },

    getHighlightedTypeIndex() {
        const highlightedType = this.getHighlightedType();
        const index = Odigeo._.findIndex(this.getAvailableOptions(), (option) => option.type === highlightedType);
        return index >= 0 ? index + 1 : '';
    },

    isDefaultOptionSelected() {
        return this.getSelectedType() === this.getDefaultType();
    },

    selectType(type, isSelectedByUser) {
        this.set('selectedType', type);
        this.set('availableOptions', this.getAvailableOptions());

        if (isSelectedByUser) {
            this.storeSelectionInSession(type);
        }
        this.triggerOptionChanged(type);
    },

    triggerOptionChanged(type) {
        Odigeo.trigger(this.widget.id + ':onBoardingOptionSelected', this.getTriggerData(type));
    },

    storeSelectionInSession(type) {
        const data = Odigeo._.set({}, this.getBookingId(), type);

        Odigeo.Session.set('boardingOptionSelection', JSON.stringify(data));
    },

    getSelectedType() {
        return this.get('selectedType') || this.getDefaultType() || '';
    },

    getDefaultType() {
        return this.get('defaultType');
    },

    getCarrierList() {
        return this.get('carriers') || [];
    },

    findByType(type) {
        return Odigeo._.find(this.getAvailableOptions(), function(option) {
            return option.type === type;
        }) || {};
    },

    getPrice(type) {
        return this.findByType(type).totalPrice;
    },

    getPrimePrice(type) {
        return this.findByType(type).totalPrimePrice;
    },

    getFee(type) {
        return this.findByType(type).fee;
    },

    getPrimeFee(type) {
        return this.findByType(type).primeFee;
    },

    getTrackingData() {
        return boardingOptionsCollection.getTrackData();
    },

    getCarrier() {
        return boardingOptionsCollection.getCarrier();
    },

    triggerUserInteraction() {
        Odigeo.trigger(this.widget.id + ':onBoardingOptionInteracted');
    },
    isNegativePrimeMarkupPriceVisible() {
        return Odigeo.AB.isNegativePrimeMarkupBagsSeatsMobile && this.getPrimeInfo().isPrimeView &&
            this.isDefaultOptionSelected() && this.getPrimeInfo().discountAmount > 0;
    },
};
